import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useMemo } from "react";
import { STORE_INFO_MAPPING, STORE_INFO_USA } from "./mapping";
import { ROUTE_MAP, REDIRECT_MAP } from "../../layout/App/mapping";

const VALID_ROUTE_PATHS = new Set([
  ...Object.values(ROUTE_MAP).flatMap((mapping) =>
    [mapping.id, mapping.path.replace(/^\//, "").split("/")[0]].filter(Boolean),
  ),
  ...REDIRECT_MAP.map(
    ({ from }) => from.replace(/^\//, "").replace(/\/\*$/, "").split("/")[0],
  ).filter(Boolean),
]);

import { Cookies } from "react-cookie";

import { allRoutes } from "./routes";
import {
  CountryCode,
  StoreId,
  Language,
  Nullish,
} from "@ubnt/store.core/lib/types";
import {
  evalCountryCode,
  evalLanguageCode,
  evalStoreId,
  DEPRECATED_LANGUAGES,
  resolveDeprecatedLang,
  saveCookie,
  BASE_PATH,
  NOT_FOUND_PATH,
} from "./utils";

// Pre-compute deprecated languages
const DEPRECATED_LANGUAGES_SET = new Set(Object.values(DEPRECATED_LANGUAGES));

import { useEffectOnce } from "react-use";
import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_LANGUAGE,
  GLOBAL_SITE_CONFIG,
  UI_COM_SITE_CONFIG,
} from "./constants";
import { SiteSettingsContextValue } from "./types";

/**
 * Changed from "country-code" to "country-code-session" to solve issue that was caused
 * with microsite language cookie PR where the country code was set to expire in 1 year
 * instead of session.
 */
export const COOKIE_COUNTRY_CODE = "country-code-session";
export const COOKIE_MICROSITE_LANGUAGE = "microsite-language";

const cookies = new Cookies();

export const LocalStorageKey = {
  StoreReferer: "store-referer",
  LanguageReferer: "language-referer",
};

export const ModalId = {
  Support: "support",
  Account: "account",
  MobileMenu: "mobileMenu",
};

export const SiteSettings = createContext<SiteSettingsContextValue | null>(
  null,
);

export const SiteSettingsProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // #############################################################################
  // Geo-IP detection
  // #############################################################################
  /**
   * null - Not processed
   * undefined - Geo-IP detection failed
   * CountryCode - Geo-IP detection succeeded
   */
  const [geoCountryCode, setGeoCountryCode] =
    useState<Nullish<CountryCode>>(null);
  /** True once geo was read from cookie or the geo-ip fetch finished (any outcome). */
  const [geoIpReady, setGeoIpReady] = useState(false);

  useEffect(() => {
    // Cookies
    const cookieCountryCode = evalCountryCode(cookies.get(COOKIE_COUNTRY_CODE));

    if (cookieCountryCode) {
      setGeoCountryCode(cookieCountryCode);
      setGeoIpReady(true);

      // Exit early if we already have stored value
      return;
    }

    let controller: AbortController | null = null;
    const runAsync = async () => {
      try {
        controller = new AbortController();

        const url = new URL(GLOBAL_SITE_CONFIG.store.defaultRegion.baseUrl);
        url.pathname = "/api/geo-ip";

        const res = await fetch(url.toString(), {
          mode: "cors",
          signal: controller.signal,
          // Semi new option (not supported on all browsers) - https://developer.mozilla.org/en-US/docs/Web/API/fetch#browser_compatibility
          priority: "high",
        });
        const geoResult = await res.json();

        // const geoStoreId = geoResult?.preferredStoreId;
        const geoCountry = geoResult?.country;

        const detectedCountryCode = evalCountryCode(geoCountry);

        if (detectedCountryCode) {
          cookies.set(COOKIE_COUNTRY_CODE, detectedCountryCode, {
            path: "/",
            domain: ".ui.com",
            expires: undefined,
            secure: true,
            sameSite: "lax",
          });
        }

        setGeoCountryCode(detectedCountryCode);
      } catch (err) {
        // Silence errors
        console.error("Failed to run geo-location", err);
      } finally {
        setGeoIpReady(true);
      }
    };

    runAsync();

    return () => controller?.abort();
  }, []);

  // #############################################################################
  // Country code
  // #############################################################################
  const countryCode = useMemo(() => {
    /**
     * If geoCountryCode is null, it means that the geo-ip detection is not yet
     * processed. If it is undefined, it means that the geo-ip detection failed.
     * In both cases, we want to fallback to the default country code.
     */
    return geoCountryCode ?? DEFAULT_COUNTRY_CODE;
  }, [geoCountryCode]);

  // #############################################################################
  // Store and language referer
  // #############################################################################
  /**
   * undefined - Not processed
   * null - Not found
   * StoreId - Found store
   */
  const [refererStoreId, setRefererStoreId] =
    useState<Nullish<StoreId>>(undefined);
  const [refererLanguage, setRefererLanguage] =
    useState<Nullish<Language>>(undefined);

  // Get the url/stored value
  useEffect(() => {
    // Handle URL value
    const urlParams = new URLSearchParams(location.search);

    // Skip if no store param
    if (urlParams.has("s") || urlParams.has("l")) {
      // Read and parse the search param
      const urlStoreId = urlParams.get("s")?.toLowerCase();
      const urlLanguage = urlParams.get("l")?.toLowerCase();

      const evaluatedStoreId = evalStoreId(urlStoreId);
      const evaluatedLanguage = evalLanguageCode(urlLanguage);

      if (evaluatedStoreId) {
        setRefererStoreId(evaluatedStoreId);

        // Add the value to local storage
        localStorage.setItem(LocalStorageKey.StoreReferer, evaluatedStoreId);
      }

      if (
        (evaluatedLanguage &&
          evaluatedStoreId &&
          UI_COM_SITE_CONFIG.regions[evaluatedStoreId]?.languages?.includes(
            evaluatedLanguage,
          )) ||
        (!evaluatedStoreId &&
          UI_COM_SITE_CONFIG.defaultRegion.languages?.includes(
            evaluatedLanguage,
          ))
      ) {
        setRefererLanguage(evaluatedLanguage);

        // Add the value to local storage
        localStorage.setItem(
          LocalStorageKey.LanguageReferer,
          evaluatedLanguage,
        );

        // Override preference cookie as well
        saveCookie(COOKIE_MICROSITE_LANGUAGE, evaluatedLanguage.toUpperCase());
      }

      // Remove the search param
      urlParams.delete("s");
      urlParams.delete("l");

      // Replace the search params
      setTimeout(() => {
        const searchString = urlParams.toString();
        const searchStringFragment = searchString
          ? `?${urlParams.toString()}`
          : BASE_PATH;
        const hashFragment = location.hash;
        const newUrl = [
          location.pathname,
          searchStringFragment,
          hashFragment,
        ].join("");

        navigate(newUrl, { replace: true });
      }, 0);

      return;
    }

    const storageStoreId = localStorage.getItem(LocalStorageKey.StoreReferer);
    const storageLanguage = localStorage.getItem(
      LocalStorageKey.LanguageReferer,
    );

    const evaluatedStoreId = evalStoreId(storageStoreId);
    const evaluatedLanguage = evalLanguageCode(storageLanguage);

    // Handle local storage value
    if (evaluatedStoreId) {
      setRefererStoreId(evaluatedStoreId);
    } else {
      // Not found
      setRefererStoreId(null);
    }

    if (storageLanguage) {
      setRefererLanguage(evaluatedLanguage);
      saveCookie(COOKIE_MICROSITE_LANGUAGE, evaluatedLanguage.toUpperCase());
    } else {
      // Not found
      setRefererLanguage(null);
    }
  }, [navigate, location.pathname, location.search, location.hash]);

  // #############################################################################
  // Parse final store
  // #############################################################################
  const store = useMemo(() => {
    // 1. Route matched a store from URL
    const routeStoreId = params?.storeId;
    const routeStore =
      routeStoreId && STORE_INFO_MAPPING.find((el) => el.id === routeStoreId);

    if (routeStore) {
      return routeStore;
    }

    // 2. By referer store
    const refererStore =
      refererStoreId &&
      STORE_INFO_MAPPING.find((el) => el.id === refererStoreId);
    if (refererStore) {
      return refererStore;
    }

    // 3. Get geo country store
    const geoStore =
      countryCode &&
      STORE_INFO_MAPPING.find((el) => el.countries.includes(countryCode));

    if (geoStore) {
      return geoStore;
    }

    // 4. Always fall back to US store if no others
    return STORE_INFO_USA;
  }, [params?.storeId, refererStoreId, countryCode]);

  const canRunUsAutoAnalyticsConsent = useMemo(() => {
    if (store.id !== StoreId.Us) {
      return true;
    }

    const routeStoreId = params?.storeId;

    if (
      routeStoreId &&
      STORE_INFO_MAPPING.some((el) => el.id === routeStoreId)
    ) {
      return true;
    }

    if (refererStoreId) {
      return true;
    }

    /**
     * Implicit US store: wait until geo-ip has settled (cookie or fetch done), without
     * overloading `geoCountryCode` null vs undefined — `geoIpReady` is set in `finally`
     * so failures still unblock OneTrust / gtag.
     */
    return geoIpReady;
  }, [store.id, params?.storeId, refererStoreId, geoIpReady]);

  // #############################################################################
  // Language cookie
  // #############################################################################
  const languageCookie = useMemo(() => {
    const languageCookie = cookies.get(COOKIE_MICROSITE_LANGUAGE);

    // If user has not specifically set a language then do nothing
    if (!languageCookie) return;

    return evalLanguageCode(languageCookie);
  }, []);

  // #############################################################################
  // Language in URL
  // #############################################################################

  // Parse language directly from pathname: /:storeId/:language or /:storeId/:language/:path
  // Also handles deprecated language resolution (es/pt -> es-es/es-mx/pt-pt/pt-br)
  const languageUrl = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    // Check if path matches pattern: /:storeId/:language or /:storeId/:language/:path...
    // Only check for language if we have at least 2 segments and the first is a valid storeId
    if (pathSegments.length <= 1) {
      return undefined;
    }

    const [firstSegment, potentialLanguage] = pathSegments;

    const evaluatedStoreId = evalStoreId(firstSegment);
    // Only treat second segment as language if first segment is a valid storeId
    if (!evaluatedStoreId) {
      return undefined;
    }

    // Check if it's a valid language code
    if (evalLanguageCode(potentialLanguage)) {
      return potentialLanguage;
    }

    // Check if it's a deprecated language (es/pt) and resolve it
    if (
      DEPRECATED_LANGUAGES_SET.has(potentialLanguage as DEPRECATED_LANGUAGES)
    ) {
      const resolvedDeprecatedLanguage = resolveDeprecatedLang(
        potentialLanguage,
        evaluatedStoreId,
      );
      if (resolvedDeprecatedLanguage) {
        return resolvedDeprecatedLanguage;
      }
    }
  }, [location.pathname]);

  // Validate storeId and languages
  // Redirect deprecated languages
  // Throw 404 if store is not valid
  // (only on initial load)
  useEffectOnce(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const [firstSegment] = pathSegments;

    // Skip validation for root path, valid route paths, or 404 path
    if (
      location.pathname === NOT_FOUND_PATH ||
      !pathSegments.length ||
      VALID_ROUTE_PATHS.has(firstSegment)
    ) {
      return undefined;
    }

    // Validate storeId if present (only if not a route)
    const storeId = params?.storeId || firstSegment;

    // If storeId is present in URL and invalid, navigate to NotFound
    if (storeId && !evalStoreId(storeId)) {
      navigate(NOT_FOUND_PATH, { replace: true });
    }

    // Handle deprecated language redirects (only on initial load)
    // Only check for deprecated languages, not invalid languages, since the second segment
    // could be a route path, not a language (e.g., /us/wifi is valid, not /us/invalid-language)
    const potentialLanguage = pathSegments[1];

    // Check if first segment is a valid storeId before checking language
    const evaluatedStoreId = evalStoreId(firstSegment);

    if (evaluatedStoreId && potentialLanguage) {
      // Only handle deprecated languages (es/pt) - don't validate other languages
      // as they might be route paths
      if (
        DEPRECATED_LANGUAGES_SET.has(potentialLanguage as DEPRECATED_LANGUAGES)
      ) {
        const resolvedLanguage = resolveDeprecatedLang(
          potentialLanguage,
          evaluatedStoreId,
        );

        if (resolvedLanguage) {
          // Reconstruct path with resolved language
          const restOfPath = pathSegments.slice(2).join("/");
          const newPath = restOfPath
            ? `/${firstSegment}/${resolvedLanguage}/${restOfPath}`
            : `/${firstSegment}/${resolvedLanguage}`;

          navigate(
            {
              pathname: newPath,
              search: location.search,
              hash: location.hash,
            },
            { replace: true },
          );
        }
      }
    }
  });

  // #############################################################################
  // Language
  // #############################################################################
  const language = useMemo<Language>(() => {
    const evaluatedLanguageUrl = languageUrl
      ? evalLanguageCode(languageUrl)
      : undefined;

    return (
      evaluatedLanguageUrl ??
      refererLanguage ??
      languageCookie ??
      DEFAULT_LANGUAGE
    );
  }, [languageCookie, languageUrl, refererLanguage]);

  // #############################################################################
  // Create full store urls
  // #############################################################################
  const routes = allRoutes(store.id, language);

  // #############################################################################
  // Routing util
  // #############################################################################
  const onLinkClick = useCallback(
    (e, destinationUrl) => {
      // Navigate to the pathname locally
      const url = new URL(destinationUrl);
      const currentUrl = new URL(window.location.href);

      const sanitizedHost = (hostname: string) =>
        hostname.toLowerCase().replace(/^www\./, BASE_PATH);

      if (
        sanitizedHost(url.hostname) === sanitizedHost(currentUrl.hostname) ||
        currentUrl.hostname === "localhost"
      ) {
        e.preventDefault();

        if (url.hash && url.pathname === currentUrl.pathname) {
          navigate({ hash: url.hash }, { replace: true });
        } else {
          navigate({ pathname: url.pathname, hash: url.hash });
        }

        if (url.hash) {
          const element = document.getElementById(
            url.hash.replace(/^#/, BASE_PATH),
          );
          if (element) {
            const sectionOffsetTop = element.offsetTop;

            window.scrollTo({
              top: sectionOffsetTop,
              behavior: "smooth",
            });
          }
        }
      }
    },
    [navigate],
  );

  /**
   * Util to get root url for default store redirect
   */
  // const getDefaultStoreLanguageRoute = useCallback(() => {
  //   const regionCode = geoCountryCode || DEFAULT_COUNTRY_CODE;

  //   // NOTE: Currently in prod we only want single language! Remove this if-statement when launching translations
  //   if (process.env.isEnvProduction) {
  //     return `/${store.id}/${DEFAULT_LANGUAGE}`;
  //   }

  //   const defaultLanguage = getDefaultLanguageByRegion(regionCode);
  //   return `/${store.id}/${defaultLanguage}`;
  // }, [geoCountryCode, store.id]);

  // #############################################################################
  // Embed
  // #############################################################################
  const isAppView = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);

    return urlParams.get("appview") === "true";
  }, [location.search]);
  // #############################################################################

  // #############################################################################
  // Result
  // #############################################################################
  return (
    <SiteSettings.Provider
      value={{
        store,
        routes,
        language,
        // getDefaultStoreLanguageRoute,
        geoCountryCode,
        canRunUsAutoAnalyticsConsent,
        isAppView,
        onLinkClick,
      }}
    >
      {children}
    </SiteSettings.Provider>
  );
};

export function useSiteSettings() {
  const context = useContext(SiteSettings);
  if (context === null) {
    throw new Error("useSiteSettings must be used within a SiteSettings");
  }
  return context;
}
